import React, { useState } from 'react';
import { 
  Rocket, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Fuel, 
  DollarSign,
  Code,
  FileJson,
  Copy,
  Check,
  ExternalLink,
  Shield,
  Clock
} from 'lucide-react';
import { SolidityCodeViewer } from './SolidityCodeViewer';
import { AuditStatusBadge } from './AuditStatusBadge';

interface GasPrice {
  slow: string;
  standard: string;
  fast: string;
  unit: string;
}

interface DeployCost {
  slow: string;
  standard: string;
  fast: string;
  currency: string;
}

interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

interface NetworkConfig {
  chainId: number;
  rpcUrl: string;
  explorerUrl: string;
  nativeCurrency: string;
}

interface DeployPreview {
  templateId: string;
  templateName: string;
  templateVersion: string;
  network: string;
  networkConfig: NetworkConfig;
  params: Record<string, any>;
  finalCode: string;
  bytecode: string;
  abi: any[];
  gasEstimate: string;
  gasPrice: GasPrice;
  estimatedCost: DeployCost;
  validationErrors: ValidationError[];
  canDeploy: boolean;
  auditStatus: 'PENDING_AUDIT' | 'AUDITED' | 'AUDIT_FAILED';
  warnings: string[];
}

interface DeployPreviewPanelProps {
  preview: DeployPreview;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

type GasSpeed = 'slow' | 'standard' | 'fast';

export const DeployPreviewPanel: React.FC<DeployPreviewPanelProps> = ({
  preview,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const [selectedSpeed, setSelectedSpeed] = useState<GasSpeed>('standard');
  const [activeTab, setActiveTab] = useState<'code' | 'bytecode' | 'abi'>('code');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getSpeedLabel = (speed: GasSpeed) => {
    switch (speed) {
      case 'slow': return 'Lento (~5 min)';
      case 'standard': return 'Normal (~2 min)';
      case 'fast': return 'Rápido (~30 seg)';
    }
  };

  const getSpeedColor = (speed: GasSpeed) => {
    switch (speed) {
      case 'slow': return 'text-blue-400';
      case 'standard': return 'text-yellow-400';
      case 'fast': return 'text-green-400';
    }
  };

  const isMainnet = !preview.network.includes('TESTNET') && 
                    !preview.network.includes('SEPOLIA') && 
                    !preview.network.includes('MUMBAI');

  return (
    <div className="flex flex-col bg-surface-900 rounded-xl border border-surface-700 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-surface-800 border-b border-surface-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-500/20">
              <Rocket className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-surface-100">Preview de Deploy</h3>
              <p className="text-sm text-surface-400">{preview.templateName} v{preview.templateVersion}</p>
            </div>
          </div>
          <AuditStatusBadge 
            status={preview.auditStatus} 
            showDetails 
          />
        </div>
      </div>


      {/* Warnings and Errors */}
      {(preview.warnings.length > 0 || preview.validationErrors.length > 0) && (
        <div className="px-6 py-4 space-y-3 border-b border-surface-700">
          {/* Warnings */}
          {preview.warnings.map((warning, index) => (
            <div key={`warning-${index}`} className="flex items-start gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-yellow-200">{warning}</span>
            </div>
          ))}

          {/* Validation Errors */}
          {preview.validationErrors.map((error, index) => (
            <div key={`error-${index}`} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="text-red-200 font-medium">{error.field}:</span>
                <span className="text-red-300 ml-2">{error.message}</span>
                {error.value !== undefined && (
                  <span className="text-red-400 ml-2">(valor: {String(error.value)})</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Network and Gas Info */}
      <div className="px-6 py-4 grid grid-cols-2 gap-6 border-b border-surface-700">
        {/* Network Info */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-surface-300 flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            Rede de Deploy
          </h4>
          <div className="p-4 rounded-lg bg-surface-800 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-400">Rede:</span>
              <span className={`text-sm font-medium ${isMainnet ? 'text-red-400' : 'text-green-400'}`}>
                {preview.network}
                {isMainnet && <span className="ml-2 text-xs">(Mainnet)</span>}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-400">Chain ID:</span>
              <span className="text-sm text-surface-200">{preview.networkConfig.chainId}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-400">Moeda:</span>
              <span className="text-sm text-surface-200">{preview.networkConfig.nativeCurrency}</span>
            </div>
          </div>
        </div>

        {/* Gas Estimate */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-surface-300 flex items-center gap-2">
            <Fuel className="w-4 h-4" />
            Estimativa de Gas
          </h4>
          <div className="p-4 rounded-lg bg-surface-800 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-400">Gas Estimado:</span>
              <span className="text-sm text-surface-200 font-mono">{parseInt(preview.gasEstimate).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-400">Gas Price ({preview.gasPrice.unit}):</span>
              <span className="text-sm text-surface-200 font-mono">{preview.gasPrice[selectedSpeed]}</span>
            </div>
          </div>
        </div>
      </div>


      {/* Cost Estimate */}
      <div className="px-6 py-4 border-b border-surface-700">
        <h4 className="text-sm font-medium text-surface-300 flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4" />
          Custo Estimado de Deploy
        </h4>
        <div className="grid grid-cols-3 gap-4">
          {(['slow', 'standard', 'fast'] as GasSpeed[]).map((speed) => (
            <button
              key={speed}
              onClick={() => setSelectedSpeed(speed)}
              className={`p-4 rounded-lg border transition-all ${
                selectedSpeed === speed
                  ? 'bg-primary-500/20 border-primary-500'
                  : 'bg-surface-800 border-surface-700 hover:border-surface-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium ${getSpeedColor(speed)}`}>
                  {getSpeedLabel(speed)}
                </span>
                {selectedSpeed === speed && (
                  <Check className="w-4 h-4 text-primary-400" />
                )}
              </div>
              <div className="text-lg font-bold text-surface-100">
                {preview.estimatedCost[speed]} {preview.estimatedCost.currency}
              </div>
              <div className="text-xs text-surface-500 mt-1">
                {preview.gasPrice[speed]} {preview.gasPrice.unit}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Parameters */}
      <div className="px-6 py-4 border-b border-surface-700">
        <h4 className="text-sm font-medium text-surface-300 mb-3">Parâmetros de Deploy</h4>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(preview.params).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-surface-800">
              <span className="text-sm text-surface-400">{key}:</span>
              <span className="text-sm text-surface-200 font-mono truncate max-w-[200px]" title={String(value)}>
                {String(value)}
              </span>
            </div>
          ))}
        </div>
      </div>


      {/* Code/Bytecode/ABI Tabs */}
      <div className="px-6 py-4 border-b border-surface-700">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setActiveTab('code')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'code'
                ? 'bg-primary-500/20 text-primary-400'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
            }`}
          >
            <Code className="w-4 h-4 inline mr-2" />
            Código Final
          </button>
          <button
            onClick={() => setActiveTab('bytecode')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'bytecode'
                ? 'bg-primary-500/20 text-primary-400'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
            }`}
          >
            <FileJson className="w-4 h-4 inline mr-2" />
            Bytecode
          </button>
          <button
            onClick={() => setActiveTab('abi')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'abi'
                ? 'bg-primary-500/20 text-primary-400'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
            }`}
          >
            <FileJson className="w-4 h-4 inline mr-2" />
            ABI
          </button>
        </div>

        {activeTab === 'code' && (
          <SolidityCodeViewer code={preview.finalCode} height="300px" readOnly />
        )}

        {activeTab === 'bytecode' && (
          <div className="relative">
            <button
              onClick={() => copyToClipboard(preview.bytecode, 'bytecode')}
              className="absolute top-2 right-2 z-10 p-2 rounded bg-surface-700 hover:bg-surface-600 transition-colors"
              title="Copiar bytecode"
            >
              {copiedField === 'bytecode' ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4 text-surface-400" />
              )}
            </button>
            <pre className="p-4 rounded-lg bg-surface-800 text-xs text-surface-300 font-mono overflow-auto max-h-[300px] break-all">
              {preview.bytecode || '(Bytecode não disponível)'}
            </pre>
          </div>
        )}

        {activeTab === 'abi' && (
          <div className="relative">
            <button
              onClick={() => copyToClipboard(JSON.stringify(preview.abi, null, 2), 'abi')}
              className="absolute top-2 right-2 z-10 p-2 rounded bg-surface-700 hover:bg-surface-600 transition-colors"
              title="Copiar ABI"
            >
              {copiedField === 'abi' ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4 text-surface-400" />
              )}
            </button>
            <pre className="p-4 rounded-lg bg-surface-800 text-xs text-surface-300 font-mono overflow-auto max-h-[300px]">
              {JSON.stringify(preview.abi, null, 2) || '[]'}
            </pre>
          </div>
        )}
      </div>


      {/* Footer with Actions */}
      <div className="px-6 py-4 bg-surface-800 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          {preview.canDeploy ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-400">Pronto para deploy</span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400">Deploy bloqueado - corrija os erros acima</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-surface-600 text-surface-300 hover:bg-surface-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={!preview.canDeploy || isLoading}
            className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              preview.canDeploy && !isLoading
                ? 'bg-primary-500 hover:bg-primary-600 text-white'
                : 'bg-surface-700 text-surface-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <Clock className="w-4 h-4 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <Rocket className="w-4 h-4" />
                Confirmar Deploy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mainnet Warning */}
      {isMainnet && preview.canDeploy && (
        <div className="px-6 py-3 bg-red-500/10 border-t border-red-500/30 flex items-center gap-3">
          <Shield className="w-5 h-5 text-red-400" />
          <span className="text-sm text-red-300">
            <strong>Atenção:</strong> Você está prestes a fazer deploy em uma rede mainnet. 
            Esta ação é irreversível e consumirá {preview.networkConfig.nativeCurrency} real.
          </span>
        </div>
      )}
    </div>
  );
};

export default DeployPreviewPanel;
